import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState } from "features/config/store";
import {
  getCompaniesRepository,
  createCompanyRepository,
  getCompanyDetailsRepository,
  updateCompanyRepository,
  deleteCompanyRepository,
} from "features/ms6/data/ms6.repository";
import { Company, CreateCompanyParam } from "features/ms6/core/ms6.param";

export enum CompanyManagementState {
  initial = "initial",
  inProgress = "inProgress",
  success = "success",
  fail = "fail",
}

interface InitialState {
  status: CompanyManagementState;
  message: string;
  companies: Company[];
  currentCompany: Company | null;
}

const initialState: InitialState = {
  status: CompanyManagementState.initial,
  message: "",
  companies: [],
  currentCompany: null,
};

// Async Thunks
export const getCompanies = createAsyncThunk(
  "companyManagement/getCompanies",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCompaniesRepository();
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch companies"
        );
      }
      return rejectWithValue("Failed to fetch companies");
    }
  }
);

export const createCompany = createAsyncThunk(
  "companyManagement/createCompany",
  async (companyData: CreateCompanyParam, { rejectWithValue }) => {
    try {
      const response = await createCompanyRepository(companyData);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to create company"
        );
      }
      return rejectWithValue("Failed to create company");
    }
  }
);

export const getCompanyDetails = createAsyncThunk(
  "companyManagement/getCompanyDetails",
  async (companyId: number, { rejectWithValue }) => {
    try {
      const response = await getCompanyDetailsRepository(companyId);
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch company details"
        );
      }
      return rejectWithValue("Failed to fetch company details");
    }
  }
);

export const updateCompany = createAsyncThunk(
  "companyManagement/updateCompany",
  async (
    {
      companyId,
      companyData,
    }: { companyId: number; companyData: CreateCompanyParam },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateCompanyRepository(companyId, companyData);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to update company"
        );
      }
      return rejectWithValue("Failed to update company");
    }
  }
);

export const deleteCompany = createAsyncThunk(
  "companyManagement/deleteCompany",
  async (companyId: number, { rejectWithValue }) => {
    try {
      const response = await deleteCompanyRepository(companyId);
      return { ...response.data, deletedId: companyId };
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to delete company"
        );
      }
      return rejectWithValue("Failed to delete company");
    }
  }
);

const companyManagementSlice = createSlice({
  name: "companyManagement",
  initialState,
  reducers: {
    resetCompanyManagement: (state) => {
      state.status = CompanyManagementState.initial;
      state.message = "";
    },
    clearCurrentCompany: (state) => {
      state.currentCompany = null;
    },
  },
  extraReducers: (builder) => {
    // Get Companies
    builder
      .addCase(getCompanies.pending, (state) => {
        state.status = CompanyManagementState.inProgress;
      })
      .addCase(getCompanies.fulfilled, (state, action) => {
        state.status = CompanyManagementState.success;
        state.companies = action.payload;
      })
      .addCase(getCompanies.rejected, (state, action) => {
        state.status = CompanyManagementState.fail;
        state.message = action.payload as string;
      });

    // Create Company
    builder
      .addCase(createCompany.pending, (state) => {
        state.status = CompanyManagementState.inProgress;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.status = CompanyManagementState.success;
        state.message = action.payload.message;
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.status = CompanyManagementState.fail;
        state.message = action.payload as string;
      });

    // Get Company Details
    builder.addCase(getCompanyDetails.fulfilled, (state, action) => {
      state.currentCompany = action.payload;
    });

    // Update Company
    builder
      .addCase(updateCompany.pending, (state) => {
        state.status = CompanyManagementState.inProgress;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.status = CompanyManagementState.success;
        state.message = action.payload.message;
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.status = CompanyManagementState.fail;
        state.message = action.payload as string;
      });

    // Delete Company
    builder
      .addCase(deleteCompany.pending, (state) => {
        state.status = CompanyManagementState.inProgress;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.status = CompanyManagementState.success;
        state.message = action.payload.message;
        state.companies = state.companies.filter(
          (company) => company.id !== action.payload.deletedId
        );
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.status = CompanyManagementState.fail;
        state.message = action.payload as string;
      });
  },
});

export const { resetCompanyManagement, clearCurrentCompany } =
  companyManagementSlice.actions;

export const selectCompanyManagement = (state: RootState) =>
  state.companyManagement;

export default companyManagementSlice.reducer;
